import type { CSSProperties, FC } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { token } from "@stubhub/broadway-foundation/tokens";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import { Content } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { FAVORITES_BUTTON2_VARIANTS } from "src/website-apps/shared/components/FavoritesV2Buttons/constants";
import { FavoritesIconSize } from "src/website-apps/shared/components/FavoritesV2Buttons/FavoritesV2Button/types";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { ArtistInfoColumn } from "src/website-apps/shared/components/hero-banner/ImmersiveHeroBanner/ArtistInfoColumn";
import {
  ButtonsContainer,
  StyledPortalFavoritesButtonV2,
  StyledPortalShareButton,
} from "src/website-apps/shared/components/hero-banner/ImmersiveHeroBanner/ImmersiveHeroBanner.styled";
import { PerformerMerchLabel } from "src/website-apps/shared/components/hero-banner/PerformerMerchLabel";
import { SHARING_ORIGINS } from "src/website-apps/shared/components/stubhub-sharing/SharingOrigins";
import {
  isPerformerCredibilityBadge,
  PerformerLabelType,
  useWillShowPerformerCredibilityLabel,
} from "src/website-apps/shared/hooks/useWillShowPerformerCredibilityLabel";
import { hasPerformerSurfaceEvents } from "src/website-apps/shared/utils/performerGridSurface";

const rowContainer = css({
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "nowrap",
});

const colWithSharingButton = css({
  width: "[650px]",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  rowGap: "primitives.150",
});

const mlbPlayoffsTitle = css({
  display: "flex",
  flexDirection: "column",
  rowGap: "primitives.100",
});

const mlbPlayoffsSubtitle = css({
  textStyle: "body.medium",
  color: "text",
});

const credibilityBadge = css({
  width: "[fit-content]",
  height: "[22px]",
  minHeight: "[22px]",
  paddingBlock: "primitives.25",
  paddingInline: "primitives.75",
  borderStartStartRadius: "element.extraSmall",
  borderStartEndRadius: "element.extraSmall",
  borderEndStartRadius: "element.extraSmall",
  borderEndEndRadius: "element.extraSmall",
});

const DESKTOP_BADGE_TEXT_STYLE = {
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "16px",
} satisfies Pick<CSSProperties, "fontSize" | "fontStyle" | "fontWeight" | "lineHeight">;

const DESKTOP_MLB_ICON_DIMENSIONS = { width: 22, height: 12 } as const;

interface Props {
  /** The name of the category. */
  categoryName: string;
  /** The ID of the category. */
  categoryId: number;
  /** Favorites count. */
  aggregateFavorites: number;
}

// Productionized: SharingFeatureV1 (TM:16995, Deployed Variant 1, StubHub only)
export const BannerTitle: FC<Props> = ({ categoryName, categoryId, aggregateFavorites }) => {
  const model = usePerformerPageModelData();
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const labelType = useWillShowPerformerCredibilityLabel();
  const hasEvents = hasPerformerSurfaceEvents(model.performerGridSurface);
  const isMlbPlayoffs = model.performerGridSurface?.type === "mlb-playoffs";
  const canMoveCredibilityBadge = isDesktop && isPerformerCredibilityBadge(labelType);
  const shouldMoveCredibilityBadge =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerCredibilityBadgePlacement,
      false,
      true,
      () => canMoveCredibilityBadge,
    ) ?? false;

  return (
    <div className={rowContainer}>
      <div className={colWithSharingButton}>
        {canMoveCredibilityBadge && shouldMoveCredibilityBadge && (
          <PerformerMerchLabel
            className={credibilityBadge}
            compactMlbLabel
            color={token(labelType === PerformerLabelType.MLB ? "colors.text.info" : "colors.text.brand")}
            partnerIconDimensions={DESKTOP_MLB_ICON_DIMENSIONS}
            textStyle={DESKTOP_BADGE_TEXT_STYLE}
          />
        )}
        {isMlbPlayoffs ? (
          <div className={mlbPlayoffsTitle}>
            <ArtistInfoColumn categoryName={categoryName} />
            <span className={mlbPlayoffsSubtitle}>
              <Content id="cid$#4401471#" /> {/* MLB Playoffs */}
            </span>
          </div>
        ) : (
          <ArtistInfoColumn categoryName={categoryName} />
        )}
      </div>

      <ButtonsContainer
        $width={150} // width of like button + share button
      >
        <StyledPortalFavoritesButtonV2
          authRedirectUrl=""
          entityId={categoryId}
          entityType={ENTITY_TYPES.CATEGORY}
          aggregateFavorites={aggregateFavorites}
          variant={FAVORITES_BUTTON2_VARIANTS.LIGHT}
          notificationIconSize={FavoritesIconSize.Large}
          stopPropagation
          preventDefault
          $position="absolute"
          $right={40}
          $onTransparent
        />

        {hasEvents && (
          <StyledPortalShareButton
            sharingOrigin={SHARING_ORIGINS.PERFORMER}
            data={{ title: categoryName }}
            $topOffset={20}
            $onTransparent
          />
        )}
      </ButtonsContainer>
    </div>
  );
};
